import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import { END_POINT } from "../../../constants/defaultValues";
import { Separator } from "../../../components/common/CustomBootstrap";
import FooterPage from "../../../containers/pages/FooterPage";
import IntlMessages from "../../../helpers/IntlMessages";
import Breadcrumb from "../../../containers/navs/Breadcrumb";
import { injectIntl } from "react-intl";
import {
  InventoryAuditForm,
  InventoryAuditList,
  InventoryAuditApplicationMenu,
} from "../../../containers/pages/stock";

const InventoryAuditPage = ({ match, intl }) => {
  const [stocks, setStocks] = useState([]);
  const [audits, setAudits] = useState([]);
  const [filteredAudits, setFilteredAudits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState(null);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);

  const loadStocks = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/stock/all-unit`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setStocks(response.data.data);
      }
    } catch (error) {
      console.error("Error loading stocks:", error);
    }
  };

  const loadAudits = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/api/inventory-audit`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setAudits(response.data.data);
        setFilteredAudits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading audits:", error);
    }
  };

  useEffect(() => {
    loadStocks();
    loadAudits();
  }, [refreshKey]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("action") === "create") {
      setShowForm(true);
    }
  }, []);

  // Apply filter
  useEffect(() => {
    if (!filter || filter.type === "all") {
      setFilteredAudits(audits);
      return;
    }

    let filtered = [...audits];

    if (filter.type === "status") {
      filtered = audits.filter((item) => item.status === filter.value);
    } else if (filter.type === "month") {
      const startOfMonth = moment()
        .subtract(filter.value, "months")
        .startOf("month");
      const endOfMonth = moment()
        .subtract(filter.value, "months")
        .endOf("month");
      filtered = audits.filter((item) => {
        const itemDate = moment(item.createdAt);
        return itemDate.isBetween(startOfMonth, endOfMonth, null, "[]");
      });
    } else if (filter.type === "discrepancy") {
      filtered = audits.filter((item) =>
        item.items?.some((i) => i.discrepancy !== 0),
      );
    }

    setFilteredAudits(filtered);
  }, [filter, audits]);

  const handleSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleViewDetail = (audit) => {
    setSelectedAudit(audit);
    setShowDetail(true);
  };

  const handleContinueAudit = () => {
    setEditingAudit(selectedAudit);
    setShowDetail(false);
    setShowForm(true);
  };

  return (
    <div className="app-row disable-text-selection">
      <div className="container-fluid">
        <Row className="mb-4">
          <Col>
            <Breadcrumb heading="menu.inventory-audit" match={match} />
            <p className="text-muted">
              <IntlMessages id="audit.title" />
            </p>
          </Col>
          <Col className="text-right">
            <Button color="primary" onClick={() => setShowForm(true)}>
              <i className="simple-icon-plus mr-2"></i>
              <IntlMessages id="audit.button.create" />
            </Button>
          </Col>
        </Row>

        <InventoryAuditList
          data={filteredAudits}
          key={refreshKey}
          onViewDetail={handleViewDetail}
        />

        <InventoryAuditForm
          isOpen={showForm}
          toggle={() => {
            setShowForm(false);
            setEditingAudit(null);
          }}
          stocks={stocks}
          onSuccess={handleSuccess}
          editingAudit={editingAudit}
        />

        {/* Modal Chi tiết Kiểm Kho */}
        <Modal
          isOpen={showDetail}
          toggle={() => setShowDetail(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowDetail(false)}>
            <IntlMessages id="audit.modal.detail.title" /> -{" "}
            {selectedAudit?.audit_code}
          </ModalHeader>
          <ModalBody>
            {selectedAudit && (
              <>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <strong>
                      <IntlMessages id="audit.date" />:
                    </strong>{" "}
                    {moment(selectedAudit.audit_date).format("DD/MM/YYYY")}
                  </div>
                  <div>
                    <Badge
                      color={
                        selectedAudit.status === "completed"
                          ? "success"
                          : "warning"
                      }
                    >
                      {selectedAudit.status === "completed" ? (
                        <IntlMessages id="audit.status.completed" />
                      ) : (
                        <IntlMessages id="audit.status.pending" />
                      )}
                    </Badge>
                  </div>
                </div>
                {selectedAudit.notes && (
                  <p className="text-muted">
                    <strong>
                      <IntlMessages id="audit.note" />:
                    </strong>{" "}
                    {selectedAudit.notes}
                  </p>
                )}
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table size="sm" hover>
                    <thead>
                      <tr>
                        <th>
                          <IntlMessages id="audit.table.stock" />
                        </th>
                        <th className="text-center">
                          <IntlMessages id="audit.table.system" />
                        </th>
                        <th className="text-center">
                          <IntlMessages id="audit.table.actual" />
                        </th>
                        <th className="text-center">
                          <IntlMessages id="audit.table.discrepancy" />
                        </th>
                        <th>
                          <IntlMessages id="audit.table.reason" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAudit.items?.map((item) => (
                        <tr key={item.id}>
                          <td>{item.stock?.name}</td>
                          <td className="text-center">
                            {item.system_quantity}
                          </td>
                          <td className="text-center">
                            {item.actual_quantity}
                          </td>
                          <td
                            className={`text-center ${
                              item.discrepancy > 0
                                ? "text-success"
                                : item.discrepancy < 0
                                  ? "text-danger"
                                  : ""
                            }`}
                          >
                            {item.discrepancy > 0 ? "+" : ""}
                            {item.discrepancy}
                          </td>
                          <td>{item.reason || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3 pt-3 border-top">
                  <strong>
                    <IntlMessages id="audit.total-discrepancy" />:
                  </strong>{" "}
                  <span
                    className={
                      selectedAudit.total_discrepancy_value > 0
                        ? "text-success"
                        : selectedAudit.total_discrepancy_value < 0
                          ? "text-danger"
                          : ""
                    }
                  >
                    {new Intl.NumberFormat("vi-VN", {
                      style: "currency",
                      currency: "VND",
                    }).format(selectedAudit.total_discrepancy_value || 0)}
                  </span>
                </div>
              </>
            )}
          </ModalBody>
          {selectedAudit && selectedAudit.status !== "completed" && (
            <ModalFooter>
              <Button color="primary" onClick={handleContinueAudit}>
                <i className="simple-icon-control-play mr-2"></i>
                <IntlMessages id="audit.button.continue" />
              </Button>
            </ModalFooter>
          )}
        </Modal>
      </div>

      <InventoryAuditApplicationMenu
        data={audits}
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      <Separator />
      <FooterPage />
    </div>
  );
};

export default injectIntl(InventoryAuditPage);
